import { MAIN_CHAT_MODE } from '@littlebird/sdk';
import { when } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import { performAttributedSend } from '@/features/chat/chat-send';
import { electronApis } from '@/features/electron-api';
import { logError } from '@/lib/errorLogging';
import { littlebirdSdk } from '@/lib/sdk-init';
import { NEW_CHAT_ID } from '@/utils/constants';

import {
  formatMeetingDebugReport,
  type MeetingDebugCard,
} from './meeting-debug.cards';

async function readDeviceId(): Promise<string | undefined> {
  try {
    return (await electronApis.system.getDeviceId()) ?? undefined;
  } catch (error) {
    logError(error, 'Failed to read the device id for a meeting debug chat');
    return undefined;
  }
}

function buildMeetingDebugChatPrompt(
  card: MeetingDebugCard,
  deviceId: string | undefined
): string {
  const from = new Date(card.startedAt).toISOString();
  const to = card.endedAt ? new Date(card.endedAt).toISOString() : 'now';
  const meetingId = card.meetingId ?? 'none, no note started';
  return [
    'Help me debug this meeting note. Ask me what went wrong first, then find the cause.',
    '',
    `Meeting id: ${meetingId}`,
    `Device id: ${deviceId ?? 'unknown'}`,
    `Time: ${from} to ${to}`,
    'The meeting debug log for this note is attached.',
    '',
    'If you can query Axiom, search the `electron-logs` dataset in that time range:',
    `- Electron rows: \`message.category == "meeting_notes"\` and \`message.log.metadata.meeting_id == "${meetingId}"\``,
    `- ContextKit rows: \`scope == "ContextKit"\` and \`device_id == "${deviceId ?? 'unknown'}"\`. Their \`message.log.metadata\` is a JSON string, so parse it first.`,
    '',
    'When we are done, offer to draft a bug report with the meeting id, what I saw, and the cause you found.',
  ].join('\n');
}

const LOG_UPLOAD_TIMEOUT_MS = 30_000;

async function createDraftWithLog(
  card: MeetingDebugCard,
  title: string,
  prompt: string
): Promise<string> {
  const { drafts } = littlebirdSdk.stores;
  const draftId = uuidv4();
  const draft = drafts.createDraft({ draftId, content: prompt });
  const logName = `meeting-debug-${(card.meetingId ?? card.key).replace(/[^\w-]/g, '_')}.txt`;
  try {
    await drafts.addFileAndUpload(
      draftId,
      new File([formatMeetingDebugReport(card, title)], logName, {
        type: 'text/plain',
      })
    );
    await when(
      () =>
        draft.files.every(
          (file) =>
            file.uploadState === 'uploaded' || file.uploadState === 'failed'
        ),
      { timeout: LOG_UPLOAD_TIMEOUT_MS }
    );
    if (draft.files.some((file) => file.uploadState !== 'uploaded')) {
      throw new Error('The meeting debug log failed to upload');
    }
    return draftId;
  } catch (error) {
    drafts.removeDraft(draft);
    throw error;
  }
}

export async function startMeetingDebugChat(
  card: MeetingDebugCard,
  title: string
): Promise<string | undefined> {
  const deviceId = await readDeviceId();
  const draftId = await createDraftWithLog(
    card,
    title,
    buildMeetingDebugChatPrompt(card, deviceId)
  );
  const dispatch = await performAttributedSend({
    threadId: NEW_CHAT_ID,
    mode: MAIN_CHAT_MODE,
    requestResponseAudio: false,
    prepareExtras: () => ({ draftId, title: `Debug: ${title}` }),
  });
  return dispatch.createdThreadId ?? undefined;
}
